import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { crearFacturaManual, getDatosClienteFactura } from "../services/facturaManualService";
import { fieldSx } from "../../egresos/components/formStyles";

const AZUL = "#005594";
const dinero = (n) => Number(n || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoy = () => new Date().toISOString().slice(0, 10);
const lineaVacia = (mes, year) => ({ detalle: "", mes_servicio: mes, year: String(year), cantidad: "1", precio: "" });

// Formulario simple para registrar la factura de un servicio: solo los datos de la factura y líneas de texto libre
// (sin ítems del catálogo). La factura se emitió fuera del sistema; aquí solo se guarda en la base de datos.
export default function FacturaManualDialog({ servicio, cat, onClose, onRegistrada }) {
  const [f, setF] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  // Prellena NIT, nombre, sucursal y moneda con los datos de facturación del servicio.
  useEffect(() => {
    let activo = true;
    getDatosClienteFactura(servicio.cod_servicio_cliente)
      .then(({ data }) => {
        if (!activo) return;
        const mesActual = cat.meses[new Date().getMonth()];
        const yearActual = new Date().getFullYear();
        setLineas([lineaVacia(mesActual, yearActual)]);
        setF({
          num_fac: "", fecha: hoy(), tipo_doc: 5, nit: data.nit || "", nombre: data.nombre_facturar || data.razon_social || "", autorizacion: "",
          sucursal: cat.sucursales.includes(data.sucursal_facturar) ? data.sucursal_facturar : cat.sucursales[0],
          mes: cat.meses[new Date().getMonth()], year: String(new Date().getFullYear()), venta: "Alquiler",
          moneda: Number(data.moneda) === 1 ? "USD" : "BS", // en cliente_servicio: 1 = dólares, 2 = bolivianos
        });
      })
      .catch((e) => activo && setError(e?.response?.data?.error || "No se pudieron cargar los datos del cliente."));
    return () => { activo = false; };
  }, [servicio, cat]);

  // Al cambiar el mes o el año de la factura, las líneas que seguían el valor anterior pasan al nuevo.
  const set = (k) => (e) => {
    const v = e.target.value;
    if (k === "mes" || k === "year") {
      const campoLinea = k === "mes" ? "mes_servicio" : "year";
      setLineas((ls) => ls.map((l) => (String(l[campoLinea]) === String(f[k]) ? { ...l, [campoLinea]: v } : l)));
    }
    setF((p) => ({ ...p, [k]: v }));
  };
  const setLinea = (i, k, v) => setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const subtotal = (l) => (Number(l.cantidad) || 0) * (Number(l.precio) || 0);
  const total = useMemo(() => lineas.reduce((a, l) => a + subtotal(l), 0), [lineas]);
  const tc = Number(cat.tipo_cambio) || 0;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await crearFacturaManual({ ...f, id_servicio_cliente: servicio.cod_servicio_cliente, detalles: lineas });
      onRegistrada(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo registrar la factura.");
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, size, extra = {}) => (
    <Grid size={size}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx} {...extra} /></Grid>
  );
  const select = (k, label, opciones, size) => (
    <Grid size={size}>
      <TextField select fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx}>
        {opciones.map((o) => <MenuItem key={o.v} value={o.v}>{o.t}</MenuItem>)}
      </TextField>
    </Grid>
  );

  return (
    <Dialog open onClose={() => !guardando && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, bgcolor: AZUL, color: "#fff", fontSize: "0.95rem", fontWeight: 800 }}>
        Registrar factura — {servicio.razon_social}
        <Typography component="span" sx={{ ml: 1, fontSize: 12, opacity: 0.85 }}>
          Servicio #{servicio.cod_servicio_cliente}{servicio.servicio_nombre ? ` · ${servicio.servicio_nombre}` : ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {!f && !error && <CircularProgress size={20} />}
        {f && (
          <>
            <Grid container spacing={0.75}>
              {campo("num_fac", "N° de factura", { xs: 6, md: 3 }, { type: "number", autoFocus: true })}
              {campo("fecha", "Fecha de la factura", { xs: 6, md: 3 }, { type: "date", slotProps: { inputLabel: { shrink: true } } })}
              {select("sucursal", "Sucursal", cat.sucursales.map((s) => ({ v: s, t: s })), { xs: 6, md: 3 })}
              {campo("autorizacion", "N° de autorización (opcional)", { xs: 6, md: 3 }, { slotProps: { htmlInput: { maxLength: 20 } } })}

              {select("tipo_doc", "Tipo de documento", cat.tipos_doc.map((t) => ({ v: t.id, t: t.nombre })), { xs: 6, md: 2 })}
              {campo("nit", "NIT / documento", { xs: 6, md: 3 }, { slotProps: { htmlInput: { maxLength: 50 } } })}
              {campo("nombre", "Nombre o razón social", { xs: 12, md: 7 }, { slotProps: { htmlInput: { maxLength: 100 } } })}

              {select("mes", "Mes facturado", cat.meses.map((m) => ({ v: m, t: m })), { xs: 6, md: 3 })}
              {campo("year", "Año", { xs: 6, md: 2 }, { type: "number" })}
              {select("venta", "Tipo de venta", cat.ventas.map((v) => ({ v, t: v })), { xs: 6, md: 3 })}
              {select("moneda", "Moneda", [{ v: "BS", t: "Bolivianos" }, { v: "USD", t: "Dólares" }], { xs: 6, md: 2 })}
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField fullWidth size="small" label="Tipo de cambio" value={cat.tipo_cambio || ""} disabled sx={fieldSx} />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: AZUL }}>DETALLE</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setLineas((ls) => [...ls, lineaVacia(f.mes, f.year)])} sx={{ py: 0, fontSize: 11 }}>Agregar línea</Button>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    {["Cant.", "Detalle", "Mes de servicio", "Año", f.moneda === "USD" ? "Precio ($us)" : "Precio (Bs)", "Subtotal", ""].map((h, i) => (
                      <TableCell key={h || i} align={i === 0 || i === 4 || i === 5 ? "right" : "left"} sx={{ fontWeight: 800, fontSize: 11, color: AZUL, bgcolor: `${AZUL}1a`, borderBottom: `2px solid ${AZUL}` }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ width: 80 }}><TextField fullWidth size="small" type="number" value={l.cantidad} onChange={(e) => setLinea(i, "cantidad", e.target.value)} sx={fieldSx} /></TableCell>
                      <TableCell><TextField fullWidth size="small" value={l.detalle} onChange={(e) => setLinea(i, "detalle", e.target.value)} sx={fieldSx} slotProps={{ htmlInput: { maxLength: 250 } }} /></TableCell>
                      <TableCell sx={{ width: 130 }}>
                        <TextField select fullWidth size="small" value={l.mes_servicio} onChange={(e) => setLinea(i, "mes_servicio", e.target.value)} sx={fieldSx}>
                          {cat.meses.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ width: 80 }}><TextField fullWidth size="small" type="number" value={l.year} onChange={(e) => setLinea(i, "year", e.target.value)} sx={fieldSx} /></TableCell>
                      <TableCell sx={{ width: 120 }}><TextField fullWidth size="small" type="number" value={l.precio} onChange={(e) => setLinea(i, "precio", e.target.value)} sx={fieldSx} /></TableCell>
                      <TableCell align="right" sx={{ width: 110, fontSize: 12 }}>{dinero(subtotal(l))}</TableCell>
                      <TableCell sx={{ width: 40 }}>
                        <IconButton size="small" color="error" disabled={lineas.length === 1} onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ mt: 1, textAlign: "right", fontWeight: 800, fontSize: 14 }}>
              {f.moneda === "USD" ? `Total: ${dinero(total)} $us  (≈ ${dinero(total * tc)} Bs)` : `Total: ${dinero(total)} Bs`}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button size="small" variant="contained" disabled={!f || guardando} onClick={guardar} sx={{ bgcolor: AZUL, "&:hover": { bgcolor: "#003f6e" } }}>
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Registrar factura"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
